import React, { useCallback } from 'react'
import { useWeb3React } from '@web3-react/core'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Token } from 'vvs-sdk'
import { Button, Flex, IconButton, AddIcon, MinusIcon, useModal, Skeleton, TokenAmount } from 'vvs-uikit'
import { useLocation } from 'react-router-dom'
import LocalBalance from 'components/LocalBalance'
import { useTranslation } from 'contexts/Localization'
import { useAppDispatch } from 'state'
import { fetchFarmUserDataAsync } from 'state/farms'
import { useFarmFromPid, useLpTokenPrice } from 'state/farms/hooks'
import { getBalanceAmount } from 'utils/formatBalance'
import { isBlindMode } from 'utils'
import { getAddress } from 'utils/addressHelpers'
import useInvalidateActualBoostFactor from 'views/Farms/hooks/useInvalidateActualBoostFactor'
import DepositModal from '../DepositModal'
import WithdrawModal from '../WithdrawModal'
import useStakeFarms from '../../hooks/useStakeFarms'
import useUnstakeFarms from '../../hooks/useUnstakeFarms'
import useStakeFarmsV2 from '../../hooks/useStakeFarmsV2'
import useUnstakeFarmsV2 from '../../hooks/useUnStakeFarmsV2'

interface FarmCardActionsProps {
  stakedBalance?: BigNumber
  tokenBalance?: BigNumber
  tokenName?: string
  pid?: number
  apr?: number
  vvsRewardsApr?: number
  displayApr?: string
  addLiquidityUrl?: string
  vvsPrice?: BigNumber
  lpLabel?: string
  token: Token
  quoteToken: Token
  version: number
  poolLiquidity: BigNumber
  rewardData?: any[]
  farms?: any[]
  isFinished?: boolean
}

const IconButtonWrapper = styled.div`
  display: flex;
  svg {
    width: 20px;
  }
`

const StakeAction: React.FC<FarmCardActionsProps> = ({
  stakedBalance,
  tokenBalance,
  tokenName,
  pid,
  apr,
  vvsRewardsApr,
  displayApr,
  addLiquidityUrl,
  vvsPrice,
  lpLabel,
  token,
  quoteToken,
  version,
  poolLiquidity,
  rewardData,
  farms,
  isFinished,
}) => {
  const { t } = useTranslation()
  const { onStake } = useStakeFarms(pid)
  const { onStake: onStakeV2 } = useStakeFarmsV2(pid)
  const { onUnstake } = useUnstakeFarms(pid)
  const { onUnstake: onUnstakeV2 } = useUnstakeFarmsV2(pid)
  const farm = useFarmFromPid(pid, version)
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()
  const lpPrice = useLpTokenPrice(tokenName, version)

  const { onInvalidatingActualBoostFactor } = useInvalidateActualBoostFactor(pid, getAddress(farm.lpAddresses))

  const handleStake = async (amount: string) => {
    if (version === 2) {
      await onStakeV2(amount)
    } else {
      await onStake(amount)
    }
    onInvalidatingActualBoostFactor()
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }

  const handleUnstake = async (amount: string) => {
    if (version === 2) {
      await onUnstakeV2(amount)
    } else {
      await onUnstake(amount)
    }
    onInvalidatingActualBoostFactor()
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }

  const displayBalance = useCallback(() => {
    const stakedBalanceBigNumber = getBalanceAmount(stakedBalance)
    if (stakedBalanceBigNumber.gt(0) && stakedBalanceBigNumber.lt(0.0000001)) {
      return '<0.0000001'
    }
    if (stakedBalanceBigNumber.gt(0)) {
      return stakedBalanceBigNumber.toFixed(8, BigNumber.ROUND_DOWN)
    }
    return stakedBalanceBigNumber.toFixed(3, BigNumber.ROUND_DOWN)
  }, [stakedBalance])

  const [onPresentDeposit] = useModal(
    <DepositModal
      max={tokenBalance}
      stakedBalance={stakedBalance}
      onConfirm={handleStake}
      tokenName={tokenName}
      lpPrice={lpPrice}
      lpLabel={lpLabel}
      apr={apr}
      vvsApr={vvsRewardsApr}
      displayApr={displayApr}
      addLiquidityUrl={addLiquidityUrl}
      vvsPrice={vvsPrice}
      token={token}
      quoteToken={quoteToken}
      poolLiquidity={poolLiquidity}
      rewardData={rewardData}
      farm={farm}
      farms={farms}
    />,
    false,
  )
  const [onPresentWithdraw] = useModal(
    <WithdrawModal
      max={stakedBalance}
      lpPrice={lpPrice}
      onConfirm={handleUnstake}
      tokenName={tokenName}
      token={token}
      quoteToken={quoteToken}
      pid={pid}
    />,
    false,
  )

  const renderStakingButtons = () => {
    return stakedBalance.eq(0) ? (
      <Button onClick={onPresentDeposit} disabled={['history'].some((item) => location.pathname.includes(item))}>
        {t('Stake LP')}
      </Button>
    ) : (
      <IconButtonWrapper>
        <IconButton variant="tertiary" onClick={onPresentWithdraw} mr="6px" data-testid="withdraw-token">
          <MinusIcon color="primary" width="14px" />
        </IconButton>
        <IconButton
          variant="tertiary"
          onClick={onPresentDeposit}
          disabled={['history'].some((item) => location.pathname.includes(item)) || isFinished}
          data-testid="deposit-token"
        >
          <AddIcon color="primary" width="14px" />
        </IconButton>
      </IconButtonWrapper>
    )
  }

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex flexDirection="column" alignItems="flex-start">
        <TokenAmount color={stakedBalance.eq(0) ? 'textDisabled' : 'text'} data-testid="staked-balance">
          {displayBalance()}
        </TokenAmount>
        {isBlindMode() ? (
          <Skeleton mt={1} width={60} />
        ) : (
          stakedBalance.gt(0) &&
          lpPrice.gt(0) && (
            <LocalBalance
              decimals={2}
              fontSize="12px"
              color="textSubtle"
              prefix="~"
              value={getBalanceAmount(lpPrice.times(stakedBalance))}
            />
          )
        )}
      </Flex>
      {renderStakingButtons()}
    </Flex>
  )
}

export default StakeAction
